// Data & Privacy panel — Settings (Me) tab.
// Surfaces three data-rights controls:
//   1. Withdraw consent
//   2. Download my data (JSON + 1h signed file links)
//   3. Delete my account and data
import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import {
  withdrawChdConsent,
  exportMyData,
  deleteMyAccount,
} from "@/lib/privacy.functions";

const GD = "#4B1F4F";
const GR = "#6E4B71";
const T = "#FF5C7A";
const TL = "#FFE4EA";
const TM = "#FFC1CD";
const WH = "#ffffff";
const RD = "#B91C1C";

const btnBase: React.CSSProperties = {
  width: "100%",
  padding: 11,
  borderRadius: 12,
  fontSize: 13,
  fontWeight: 500,
  cursor: "pointer",
  border: `1.5px solid ${TM}`,
  background: WH,
  color: GD,
};

export function DataPrivacyPanel() {
  const callWithdraw = useServerFn(withdrawChdConsent);
  const callExport = useServerFn(exportMyData);
  const callDelete = useServerFn(deleteMyAccount);

  const [busy, setBusy] = useState<null | "withdraw" | "export" | "delete">(null);
  const [confirmWithdraw, setConfirmWithdraw] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteText, setDeleteText] = useState("");

  async function handleWithdraw() {
    setBusy("withdraw");
    try {
      await callWithdraw();
      toast.success("Consent withdrawn. Signing you out…");
      await supabase.auth.signOut();
      window.location.href = "/";
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not withdraw consent");
      setBusy(null);
    }
  }

  async function handleExport() {
    setBusy("export");
    try {
      const res = await callExport();
      const blob = new Blob([JSON.stringify(res, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `knowmeno-data-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Download started. File links inside expire in 1 hour.");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not export data");
    } finally {
      setBusy(null);
    }
  }

  async function handleDelete() {
    setBusy("delete");
    try {
      const res = await callDelete();
      if (!res.ok) {
        if (res.step === "storage") {
          toast.error(
            "Deletion didn't fully complete. Please try again. If it keeps happening, email support@knowmeno.com.",
            { duration: 12000 },
          );
        } else {
          toast.error(`Delete failed at step "${res.step}". Please contact support@knowmeno.com.`);
        }
        setBusy(null);
        return;
      }
      if (res.stripeCancelled === false) {
        toast.message(
          "Your data is deleted. We could not auto-cancel your subscription — please email support@knowmeno.com so we can stop billing.",
          { duration: 12000 },
        );
      } else {
        toast.success("Your account and data have been deleted.");
      }
      await supabase.auth.signOut();
      window.location.href = "/";
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not delete account");
      setBusy(null);
    }
  }

  return (
    <div style={{ background: TL, borderRadius: 18, padding: 16, marginBottom: 14, border: `1.5px solid ${TM}` }}>
      <div style={{ fontSize: 12, color: GD, lineHeight: 1.55, marginBottom: 14 }}>
        These controls implement the rights described in our{" "}
        <a href="/consumer-health-privacy" style={{ color: T, textDecoration: "underline" }}>
          Consumer Health Data Privacy Policy
        </a>
        . Analytics, error monitoring, and billing data held by our processors (PostHog, Sentry, Stripe) are handled
        separately — email{" "}
        <a href="mailto:support@knowmeno.com" style={{ color: T, textDecoration: "underline" }}>
          support@knowmeno.com
        </a>{" "}
        for those.
      </div>

      {/* 1. Withdraw consent */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: GD, marginBottom: 4 }}>Withdraw consent</div>
        <div style={{ fontSize: 12, color: GD, lineHeight: 1.55, marginBottom: 8 }}>
          Stops new processing of your consumer health data. You will be signed out and can no longer use the tracker
          until you grant consent again.
        </div>
        {!confirmWithdraw ? (
          <button style={btnBase} disabled={!!busy} onClick={() => setConfirmWithdraw(true)}>
            Withdraw my consent
          </button>
        ) : (
          <div style={{ display: "flex", gap: 8 }}>
            <button style={btnBase} disabled={!!busy} onClick={() => setConfirmWithdraw(false)}>
              Cancel
            </button>
            <button
              style={{ ...btnBase, background: T, color: WH, border: `1.5px solid ${T}` }}
              disabled={!!busy}
              onClick={handleWithdraw}
            >
              {busy === "withdraw" ? "Working…" : "Yes, withdraw"}
            </button>
          </div>
        )}
      </div>

      {/* 2. Download my data */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: GD, marginBottom: 4 }}>Download my data</div>
        <div style={{ fontSize: 12, color: GD, lineHeight: 1.55, marginBottom: 8 }}>
          Get a JSON file with your tracked data and signed download links for any uploaded files. The file links inside
          expire in 1 hour.
        </div>
        <button style={btnBase} disabled={!!busy} onClick={handleExport}>
          {busy === "export" ? "Preparing…" : "Download my data (JSON)"}
        </button>
      </div>

      {/* 3. Delete account */}
      <div>
        <div style={{ fontSize: 14, fontWeight: 600, color: RD, marginBottom: 4 }}>Delete my account and data</div>
        <div style={{ fontSize: 12, color: GD, lineHeight: 1.55, marginBottom: 8 }}>
          Permanently deletes your profile, tracked health data, wearable connections, uploaded labs, notifications, and
          referrals. We&apos;ll attempt to cancel an active subscription; if that fails, email{" "}
          <a href="mailto:support@knowmeno.com" style={{ color: T, textDecoration: "underline" }}>
            support@knowmeno.com
          </a>
          . Billing records that we are legally required to keep are retained. This cannot be undone.
        </div>
        {!confirmDelete ? (
          <button
            style={{ ...btnBase, color: RD, border: `1.5px solid ${RD}` }}
            disabled={!!busy}
            onClick={() => setConfirmDelete(true)}
          >
            Delete my account
          </button>
        ) : (
          <div>
            <div style={{ fontSize: 12, color: GD, marginBottom: 6 }}>
              Type <strong>DELETE</strong> to confirm:
            </div>
            <input
              value={deleteText}
              onChange={(e) => setDeleteText(e.target.value)}
              placeholder="DELETE"
              style={{
                width: "100%",
                padding: 10,
                borderRadius: 10,
                border: `1.5px solid ${TM}`,
                marginBottom: 8,
                fontSize: 13,
                color: GD,
                background: WH,
              }}
            />
            <div style={{ display: "flex", gap: 8 }}>
              <button
                style={btnBase}
                disabled={!!busy}
                onClick={() => {
                  setConfirmDelete(false);
                  setDeleteText("");
                }}
              >
                Cancel
              </button>
              <button
                style={{
                  ...btnBase,
                  background: RD,
                  color: WH,
                  border: `1.5px solid ${RD}`,
                  opacity: deleteText === "DELETE" && !busy ? 1 : 0.5,
                }}
                disabled={deleteText !== "DELETE" || !!busy}
                onClick={handleDelete}
              >
                {busy === "delete" ? "Deleting…" : "Permanently delete"}
              </button>
            </div>
          </div>
        )}
      </div>

      <div style={{ fontSize: 11, color: GR, lineHeight: 1.5, marginTop: 12 }}>
        Not medical advice. KnowMeno does not diagnose, treat, or cure any condition.
      </div>
    </div>
  );
}
